"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { useEffect, useRef, useState, type ComponentProps, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";
import { spring } from "@/lib/motion";
import { Icon, type IconName } from "./Icon";

/* ---------------------------------------------------------------- Button */

type ButtonVariant = "primary" | "secondary" | "ghost" | "outline" | "danger";
type ButtonSize = "sm" | "md" | "field" | "lg";

const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-brand text-fg-on-primary hover:bg-brand-bold",
  secondary: "bg-elevation-2 text-fg hover:bg-elevation-3",
  ghost: "bg-transparent text-fg-subtle hover:text-fg hover:bg-elevation-2",
  outline: "border border-brand text-fg hover:bg-brand-bg-soft",
  danger: "bg-error text-fg-on-primary hover:brightness-110",
};

const BUTTON_SIZE: Record<ButtonSize, string> = {
  sm: "h-[30px] px-[8px] text-[12px] leading-[1.6] rounded-md gap-[8px]",
  md: "h-[38px] px-[12px] text-[12px] leading-[1.6] rounded-lg gap-[8px]",
  /* The kit's 42px button — the one that sits in a settings row or under a
     form, level with a field. */
  field: "h-[42px] px-[16px] text-[14px] leading-[1.6] capitalize tracking-[0.28px] rounded-lg gap-[8px]",
  lg: "h-[46px] px-[16px] text-[14px] leading-[1.6] capitalize tracking-[0.28px] rounded-lg gap-[8px]",
};

export function Button({
  variant = "primary",
  size = "md",
  icon,
  iconRight,
  className,
  children,
  ...rest
}: Omit<ComponentProps<typeof motion.button>, "children"> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  iconRight?: IconName;
  children?: ReactNode;
}) {
  return (
    <motion.button
      data-pill=""
      whileHover={{ y: -1 }}
      whileTap={{ scale: 0.97, y: 0 }}
      transition={spring}
      className={cn(
        "inline-flex items-center justify-center font-bold transition-colors duration-200 select-none",
        "disabled:opacity-40 disabled:pointer-events-none",
        BUTTON_VARIANT[variant],
        BUTTON_SIZE[size],
        className,
      )}
      {...rest}
    >
      {icon && <Icon name={icon} size={size === "sm" ? 12 : 16} />}
      {children}
      {iconRight && <Icon name={iconRight} size={size === "sm" ? 12 : 16} />}
    </motion.button>
  );
}

/* ------------------------------------------------------------ IconButton */

export function IconButton({
  name,
  size = 38,
  active,
  className,
  label,
  ...rest
}: Omit<ComponentProps<typeof motion.button>, "children"> & {
  name: IconName;
  size?: number;
  active?: boolean;
  label: string;
}) {
  return (
    <motion.button
      aria-label={label}
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 0.94 }}
      transition={spring}
      style={{ width: size, height: size }}
      className={cn(
        "grid place-items-center rounded-lg transition-colors duration-200 shrink-0",
        active ? "bg-elevation-3 text-fg" : "bg-elevation-2 text-fg-muted hover:bg-elevation-3 hover:text-fg",
        className,
      )}
      {...rest}
    >
      <Icon name={name} size={Math.round(size * 0.53)} />
    </motion.button>
  );
}

/* ------------------------------------------------------------------ Chip */

/* The kit publishes three tag geometries: the 16px tile tag, the 26px pill on
   promotion cards, and the 30px tag in the in-game header. */
const CHIP_SIZE = {
  sm: "h-[16px] gap-[2px] rounded-sm px-[3px] text-[9px] leading-[1.5]",
  md: "h-[26px] gap-[4px] rounded-rounded px-[8px] py-[2px] text-[12px] leading-[1.6]",
  lg: "h-[30px] gap-[4px] rounded-md px-[12px] text-[9px] leading-[1.5]",
} as const;

export type ChipTone =
  | "neutral"
  | "brand"
  | "danger"
  | "success"
  | "error"
  | "warning"
  | "highlight"
  | "brand-accent"
  | "subtle"
  | "outline";

export function Chip({
  children,
  tone = "neutral",
  size = "sm",
  className,
}: {
  children: ReactNode;
  tone?: ChipTone;
  size?: keyof typeof CHIP_SIZE;
  className?: string;
}) {
  /* The kit only publishes two tag fills — elevation-3 and accent/error. The
     other accents pair their own fg token with the neutral fill, which is what
     keeps 9px text legible. */
  const tones = {
    neutral: "bg-elevation-3 text-fg",
    brand: "bg-brand text-fg-on-primary",
    danger: "bg-error text-fg",
    success: "bg-elevation-3 text-success-fg",
    error: "bg-elevation-3 text-error-fg",
    warning: "bg-elevation-3 text-warning-fg",
    highlight: "bg-elevation-3 text-highlight",
    "brand-accent": "bg-elevation-3 text-brand-fg-accent",
    subtle: "bg-elevation-3 text-fg-subtle",
    outline: "border border-elevation-3 text-fg-subtle",
  }[tone];
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center font-bold whitespace-nowrap",
        CHIP_SIZE[size],
        tones,
        className,
      )}
    >
      {children}
    </span>
  );
}

/* ----------------------------------------------------------------- Badge */

export function Badge({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex h-[17px] min-w-[18px] items-center justify-center rounded-rounded bg-brand px-[6px] py-[2px] text-[9px] font-bold leading-[1.5] text-fg-on-primary",
        className,
      )}
    >
      {children}
    </span>
  );
}

/* ---------------------------------------------------------------- Avatar */

export function Avatar({ name, size = 20, glow }: { name: string; size?: number; glow?: boolean }) {
  /* Deterministic hue per player so the same handle keeps its colour
     everywhere it appears — chat, feed, leaderboard. */
  const hue = [...name].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  return (
    <span
      className={cn("relative inline-grid place-items-center rounded-full shrink-0 overflow-hidden", glow && "glow-brand-soft")}
      style={{
        width: size,
        height: size,
        background: `radial-gradient(circle at 30% 25%, hsl(${hue} 85% 62%), hsl(${(hue + 40) % 360} 80% 30%) 70%)`,
        boxShadow: `inset 0 0 0 1px hsl(${hue} 60% 70% / 0.4)`,
      }}
    >
      <span
        className="font-black text-white/90 leading-none"
        style={{ fontSize: Math.max(7, size * 0.42) }}
      >
        {name.slice(0, 1).toUpperCase()}
      </span>
    </span>
  );
}

/* ----------------------------------------------------------- ProgressBar */

const PROGRESS_FILL =
  "linear-gradient(0deg, rgba(0, 0, 0, 0.25) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(90deg, var(--color-brand) 0%, var(--color-brand) 100%)";

export function ProgressBar({
  value,
  height = 30,
  striped,
  label,
  chevron,
  className,
}: {
  value: number;
  height?: number;
  striped?: boolean;
  label?: ReactNode;
  chevron?: boolean;
  className?: string;
}) {
  const labelOverFill = value >= 50;
  const chevronOverFill = value >= 90;
  return (
    <div
      className={cn(
        "relative flex w-full items-center overflow-hidden rounded-rounded bg-elevation-3",
        height >= 20 ? "px-[8px]" : "px-0",
        className,
      )}
      style={{ height }}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <motion.span
        initial={{ width: 0 }}
        whileInView={{ width: height >= 20 ? `calc(${value}% - 6px)` : `${value}%` }}
        viewport={{ once: true }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        className="absolute rounded-rounded"
        style={{
          left: height >= 20 ? 3 : 0,
          top: height >= 20 ? 3 : 0,
          height: height >= 20 ? height - 6 : height,
          backgroundImage: PROGRESS_FILL,
        }}
      >
        {striped && <span className="stripe-progress absolute inset-0 rounded-rounded opacity-70" />}
      </motion.span>
      {/* The kit's pill carries a centred label and a chevron. A bar too short
          to hold them is just the track, so nothing overflows it. */}
      {(label || chevron) && height >= 20 && (
        <span className="relative h-[14px] flex-1">
          {label && (
            <span
              className={cn(
                "absolute left-1/2 top-0 flex h-[14px] -translate-x-1/2 items-center justify-center whitespace-nowrap text-[9px] font-bold leading-[1.5]",
                labelOverFill ? "text-fg-on-primary" : "text-fg",
              )}
            >
              {label}
            </span>
          )}
          {chevron && (
            <span className="absolute right-0 top-0 flex h-[14px] items-center">
              <Icon name="chevron-right" size={14} className={chevronOverFill ? "text-fg-on-primary" : "text-fg"} />
            </span>
          )}
        </span>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- Switch */

export function Switch({
  on,
  onToggle,
  disabled,
  label,
  size = "md",
}: {
  on: boolean;
  onToggle: (next: boolean) => void;
  disabled?: boolean;
  label?: string;
  /* `lg` is the kit's 57×30 toggle on the account settings rows. */
  size?: "md" | "lg";
}) {
  const large = size === "lg";

  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onToggle(!on)}
      className={cn(
        "flex shrink-0 items-center rounded-rounded transition-colors duration-200",
        large ? "h-[30px] w-[57px] p-[4px]" : "h-6 w-11 p-[3px]",
        on ? "bg-brand" : large ? "bg-elevation-3" : "bg-elevation-4",
        disabled && "opacity-45",
      )}
    >
      <motion.span
        layout
        transition={spring}
        className={cn(
          "rounded-full",
          large ? "h-[22px] w-[22px]" : "h-[18px] w-[18px]",
          large && !on ? "bg-fg-subtle" : "bg-white",
        )}
        style={{ marginLeft: on ? (large ? 27 : 20) : 0 }}
      />
    </button>
  );
}

/* --------------------------------------------------------------- Tooltip */

type TooltipSide = "top" | "bottom" | "right";

const TOOLTIP_GAP = 8;

/* Drawn through a portal, the way the whitelabel's Radix tooltip is. A bubble
   that lives inside its trigger is clipped by the first scrolling or
   overflow-hidden ancestor, which is why the level crests in the left rail —
   the first thing inside a scrolling column — never showed theirs. The
   trigger is measured when the pointer (or focus) arrives and the bubble is
   fixed to the viewport from there; any scroll or resize drops it rather than
   leaving it floating where the trigger used to be. */
export function Tooltip({
  children,
  content,
  side = "top",
  className,
}: {
  children: ReactNode;
  content: ReactNode;
  side?: TooltipSide;
  /* On the wrapper, for when the wrapped thing has to keep taking part in
     its parent's layout — a bar that is `flex-1`, say. */
  className?: string;
}) {
  const anchor = useRef<HTMLSpanElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const show = () => {
    if (anchor.current) setRect(anchor.current.getBoundingClientRect());
  };
  const hide = () => setRect(null);

  useEffect(() => {
    if (!rect) return;

    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);

    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [rect]);

  return (
    <span
      ref={anchor}
      className={cn("relative inline-flex", className)}
      onPointerEnter={show}
      onPointerLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {rect &&
        createPortal(
          <span
            role="tooltip"
            style={
              side === "right"
                ? {
                    left: rect.right + TOOLTIP_GAP,
                    top: rect.top + rect.height / 2,
                    transform: "translate(0, -50%)",
                  }
                : {
                    left: rect.left + rect.width / 2,
                    top: side === "top" ? rect.top - TOOLTIP_GAP : rect.bottom + TOOLTIP_GAP,
                    transform: side === "top" ? "translate(-50%, -100%)" : "translate(-50%, 0)",
                  }
            }
            className="pointer-events-none fixed z-[80]"
          >
            <motion.span
              initial={side === "right" ? { opacity: 0, x: -4 } : { opacity: 0, y: side === "top" ? 4 : -4 }}
              animate={side === "right" ? { opacity: 1, x: 0 } : { opacity: 1, y: 0 }}
              transition={{ duration: 0.15 }}
              className="block whitespace-nowrap rounded-md bg-elevation-3 px-2.5 py-1.5 text-[12px] font-medium text-fg shadow-lg"
            >
              {content}
            </motion.span>
          </span>,
          document.body,
        )}
    </span>
  );
}

/* ------------------------------------------------------------- SurfaceCard */

export function Panel({
  children,
  className,
  as: As = "section",
}: {
  children: ReactNode;
  className?: string;
  as?: "section" | "div" | "article";
}) {
  return <As className={cn("rounded-2xl bg-elevation-1", className)}>{children}</As>;
}

export function PanelHeader({
  icon,
  title,
  action,
}: {
  icon?: IconName;
  title: string;
  action?: ReactNode;
}) {
  return (
    <header className="relative flex h-[62px] shrink-0 flex-col items-start gap-[4px] rounded-t-2xl bg-elevation-1 px-[16px] py-[12px]">
      <div className="flex w-full items-center gap-[8px]">
        <div className="flex min-h-[38px] min-w-0 flex-1 items-center gap-[12px]">
          {icon && <Icon name={icon} size={20} className="shrink-0 text-brand-bold" />}
          <h2 className="flex-1 truncate text-[15.75px] font-black leading-[1.6] tracking-normal lg:text-[17.5px]">{title}</h2>
        </div>
        {action && <div className="flex shrink-0 items-center justify-end gap-[8px]">{action}</div>}
      </div>
      <span className="absolute inset-x-0 bottom-0 h-px bg-border" />
    </header>
  );
}

/* ------------------------------------------------------------- LinkButton */

/* A link that dresses as a Button, through the same variant and size maps, so
   an action that is really a navigation — a reward's Play, which is a route
   to a game — sits beside a Claim button without looking like a different
   control. Defaults reproduce the previous fixed look (secondary, md), so
   existing callers are unchanged. */
export function LinkButton({
  href,
  variant = "secondary",
  size = "md",
  icon,
  children,
  className,
  ...rest
}: Omit<ComponentProps<typeof Link>, "children"> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <Link
      href={href}
      className={cn(
        "inline-flex items-center justify-center font-bold transition-colors duration-200 select-none",
        BUTTON_VARIANT[variant],
        BUTTON_SIZE[size],
        className,
      )}
      {...rest}
    >
      {icon && <Icon name={icon} size={size === "sm" ? 12 : 16} />}
      {children}
    </Link>
  );
}
